import { useRecoilValue } from 'recoil';

import { cn } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import { kioskHealthState } from 'components/state';
import Status from 'components/shared/Status/Status';
import BatteryRing from 'components/shared/BatteryRing/BatteryRing';

/**
 * Compact battery readout for a kiosk: the kiosk (iPad) battery on top and the
 * payment terminal battery below, each shown as an icon next to a circular
 * gauge. Reads the live `kioskHealthState` atom (populated by
 * useKioskHealthMonitor), so a device with no current reading renders greyed
 * out with a "~". Pass `showLabels` for the roomier admin/edit layout.
 */
const KioskBatteryStatus = ({ kioskId, showLabels = false, size = 30, className }) => {
  const health = useRecoilValue(kioskHealthState);
  const entry = health?.[kioskId];
  const online = !!entry && entry.online !== false;

  const terminalConnected = online && Number.isFinite(entry?.terminalBattery);

  const rows = [
    {
      key: 'kiosk',
      icon: 'kiosk-mono',
      label: 'Kiosk',
      percentage: entry?.kioskBattery,
      connected: online && Number.isFinite(entry?.kioskBattery),
      charging: entry?.kioskBatteryState === 'charging' || entry?.kioskBatteryState === 'full'
    },
    {
      key: 'terminal',
      icon: 'card-pos-mono',
      label: 'Terminal',
      percentage: entry?.terminalBattery,
      connected: terminalConnected,
      // A paired payment terminal draws power through its connection to the kiosk,
      // so whenever it's connected we surface it as charging (it also still
      // reflects an explicit charging flag when one is reported).
      charging: terminalConnected || !!entry?.terminalCharging
    }
  ];

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {rows.map((row) => (
        <div key={row.key} className="flex items-center gap-2">
          <Icon
            icon={row.icon}
            size={18}
            color={row.connected ? 'primary' : 'neutral'}
            className="shrink-0"
          />
          {showLabels && (
            <span
              className={cn(
                'text-sm font-500',
                row.connected ? 'text-neutral-900' : 'text-neutral-400'
              )}>
              {row.label}
            </span>
          )}
          <BatteryRing
            percentage={row.percentage}
            connected={row.connected}
            charging={row.charging}
            size={size}
            className={showLabels ? 'ml-auto' : ''}
          />
          {showLabels && row.connected && row.charging && (
            <Status status="Charging" color="success" size="small" />
          )}
        </div>
      ))}
    </div>
  );
};

export default KioskBatteryStatus;
